import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  Box,
  Link,
  CircularProgress,
  Alert
} from '@mui/material';
import SecurityTwoToneIcon from '@mui/icons-material/SecurityTwoTone';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import useAuth from 'src/hooks/useAuth';
import ConsentService from 'src/services/Consent.service';

const CONSENT_SESSION_KEY = 'ventel_dpdp_consented';

function ConsentPromptModal() {
  const { t } = useTranslation();
  const { isAuthenticated, user, logout } = useAuth();

  const [isOpen, setIsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [productUpdates] = useState(false);

  const checkUserConsent = useCallback(async () => {
    if (!isAuthenticated || !user) return;

    // Check if session flag is set for speed
    if (sessionStorage.getItem(CONSENT_SESSION_KEY) === 'true') {
      return;
    }

    try {
      const res = await ConsentService.getMyConsent();
      const responseData = res?.data || res;
      const consent = responseData?.consent || responseData;
      const hasConsented = Boolean(
        responseData?.hasConsented ??
        (consent && consent.consentGivenAt && !consent.withdrawnAt)
      );

      // If user has an active, non-withdrawn consent record
      if (hasConsented && consent && !consent.withdrawnAt && consent.status !== 'withdrawn') {
        sessionStorage.setItem(CONSENT_SESSION_KEY, 'true');
        setIsOpen(false);
      } else if (consent && (consent.withdrawnAt || consent.status === 'withdrawn')) {
        // User withdrew consent - do not show consentModal, logout directly
        setIsOpen(false);
        sessionStorage.removeItem(CONSENT_SESSION_KEY);
        await logout();
        window.location.href = '/';
      } else {
        // Existing user needs to consent
        setIsOpen(true);
      }
    } catch (err) {
      // If endpoint returns 404 or no consent found, prompt existing user
      console.warn('Consent record check:', err);
      setIsOpen(true);
    }
  }, [isAuthenticated, user, logout]);

  useEffect(() => {
    checkUserConsent();
  }, [checkUserConsent]);

  const handleAcceptConsent = async () => {
    if (!termsAccepted || !privacyAccepted) return;

    setSubmitting(true);
    const toastId = 'prompt_consent_toast';
    toast.loading(t('Saving consent preferences...'), { id: toastId });

    try {
      const purposes = [
        'account management',
        'SIM and device connectivity',
        'location tracking of my devices',
        'billing'
      ];
      if (productUpdates) {
        purposes.push('product updates by email');
      }

      await ConsentService.createConsent({
        userId: user?.id || user?._id,
        purposes,
        noticeVersion: 'v1.0',
        channel: 'web',
        consentGivenAt: new Date().toISOString()
      });

      sessionStorage.setItem(CONSENT_SESSION_KEY, 'true');
      toast.success(t('Consent recorded successfully.'), { id: toastId });
      setIsOpen(false);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.message || t('Failed to save consent. Please try again.'),
        { id: toastId }
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      setIsOpen(false);
    } catch (err) {
      console.error(err);
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog
      open={isOpen}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          p: 2,
          borderRadius: 2
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
        <SecurityTwoToneIcon color="primary" fontSize="large" />
        <Box>
          <Typography variant="h3">
            {t('DPDP Consent & Privacy Notice Update')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('Digital Personal Data Protection Act, 2023 Compliance')}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ my: 1 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          {t(
            'Ventel has updated its terms and personal data processing notices to comply with the DPDP regulations. Please review the processing purposes and provide your consent to continue using your account.'
          )}
        </Alert>

        <Box display="flex" flexDirection="column" gap={1.5} mt={1}>
          <FormControlLabel
            control={
              <Checkbox
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                {t('I accept the')}{' '}
                <Link
                  component="a"
                  href="/public/term-of-use"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('Terms of Use')}
                </Link>
              </Typography>
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={privacyAccepted}
                onChange={(e) => setPrivacyAccepted(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                {t('I have read the')}{' '}
                <Link
                  component="a"
                  href="/public/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('Privacy Notice')}
                </Link>{' '}
                {t(
                  'and consent to Ventel processing my personal data for: account management, SIM and device connectivity, location tracking of my devices, and billing.'
                )}
              </Typography>
            }
          />

          {/* <FormControlLabel
            control={
              <Checkbox
                checked={productUpdates}
                onChange={(e) => setProductUpdates(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                {t(
                  '(Optional) I consent to receive product updates and service communications by email.'
                )}
              </Typography>
            }
          /> */}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between' }}>
        <Button onClick={handleLogout} color="inherit" disabled={submitting}>
          {t('Sign out')}
        </Button>
        <Button
          onClick={handleAcceptConsent}
          color="primary"
          variant="contained"
          disabled={!termsAccepted || !privacyAccepted || submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : null}
        >
          {t('Accept and Continue')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ConsentPromptModal;
